import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";

const BmiPage = () => {
  const [age, setAge] = useState<number | "">("");
  const [weight, setWeight] = useState<number | "">("");
  const [height, setHeight] = useState<number | "">("");

  const [bmi, setBmi] = useState<number | null>(null);
  const [error, setError] = useState("");

  // ✅ Fetch current user data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("/hms/php/get_user_data.php", {
          method: "GET",
          credentials: "include",
        });

        const data = await response.json();

        if (data.status === "success") {
          setAge(Number(data.user.age));
          setWeight(Number(data.user.weight));
          setHeight(Number(data.user.height));
          setBmi(Number(data.user.bmi));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, []);

  // ✅ Calculate BMI (button)
  const calculateBmi = () => {
    if (!age || !weight || !height) {
      setError("Please fill all fields.");
      return;
    }

    const heightM = height / 100;
    const bmiValue = parseFloat((weight / (heightM * heightM)).toFixed(2));
    setBmi(bmiValue);
    setError("");
  };

  // ✅ Update backend (button)
  const updateHealthDetails = async () => {
    if (!age || !weight || !height) {
      setError("Please fill all fields before updating.");
      return;
    }

    try {
      const response = await fetch("/hms/php/update_health.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ age, weight, height }),
      });

      const data = await response.json();

      if (data.status === "success") {
        alert("✅ Updated successfully!");
      } else {
        alert(data.message || "Update failed.");
      }
    } catch (error) {
      console.error("Update Error:", error);
      alert("Connection error. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <h1 className="text-3xl font-bold mb-6">Update Health Details</h1>

        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="number"
            placeholder="Age"
            value={age}
            onChange={(e) => setAge(e.target.value === "" ? "" : Number(e.target.value))}
            className="px-3 py-2 rounded-md border border-muted"
          />
          <input
            type="number"
            placeholder="Weight (kg)"
            value={weight}
            onChange={(e) => setWeight(e.target.value === "" ? "" : Number(e.target.value))}
            className="px-3 py-2 rounded-md border border-muted"
          />
          <input
            type="number"
            placeholder="Height (cm)"
            value={height}
            onChange={(e) => setHeight(e.target.value === "" ? "" : Number(e.target.value))}
            className="px-3 py-2 rounded-md border border-muted"
          />

          <Button onClick={calculateBmi}>Recalculate BMI</Button>
        </div>

        <Button onClick={updateHealthDetails} variant="secondary" className="mb-6">
          Update Health Details
        </Button>

        {error && <p className="text-red-500 mb-4">{error}</p>}

        {bmi !== null && (
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle>Your Current BMI</CardTitle>
              <CardDescription>This is your latest calculated BMI</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{bmi}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BmiPage;
